import * as React from 'react';
import { flushSync } from 'react-dom';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';

interface RendererProps {
    targetNodeId?: string;

    [key: string]: unknown;
}

type CallbackFunction = () => void;

interface UpdatableComponent extends React.Component {
    updateWithNewProps: (newProps: Record<string, unknown>) => void;
    disconnectObserver: () => void;
}

class ReactRenderer {
    private static nodes: HTMLElement[] = [];

    private static components: UpdatableComponent[] = [];

    private static roots = new Map<HTMLElement, Root>();

    public static unmount(node: HTMLElement): void {
        const root = ReactRenderer.roots.get(node);

        ReactRenderer.nodes = ReactRenderer.nodes.filter((existingNode) => existingNode !== node);
        ReactRenderer.roots.delete(node);

        if (!root) {
            console.warn(`Failed to unmount the component at node: ${node.id}`);
            return;
        }

        root.unmount();
    }

    public static unmountAll(): void {
        if (ReactRenderer.nodes.length === 0) {
            return;
        }

        ReactRenderer.components.forEach((component) => component.disconnectObserver());
        ReactRenderer.components = [];

        ReactRenderer.nodes.forEach((node) => ReactRenderer.unmount(node));
        ReactRenderer.nodes = [];
    }

    public static render<T extends object>(
        component: React.ComponentType<T>,
        props: T,
        targetNode: HTMLElement,
        callback?: CallbackFunction,
    ): React.Component | null {
        let componentInstance: React.Component | null = null;
        let root = ReactRenderer.roots.get(targetNode);

        if (!root) {
            root = createRoot(targetNode);
            ReactRenderer.roots.set(targetNode, root);
            ReactRenderer.nodes.push(targetNode);
        }

        // eslint-disable-next-line @eslint-react/dom-no-flush-sync
        flushSync(() => {
            root.render(
                React.createElement(component, {
                    ...props,
                    ref: (instance: React.Component | null) => {
                        componentInstance = instance;
                    },
                } as T & { ref: (instance: React.Component | null) => void }),
            );
        });

        callback?.();

        return componentInstance;
    }

    public static embed<T extends object>(
        component: React.ComponentType<T>,
        props: RendererProps,
        callback?: CallbackFunction,
    ): React.Component | null {
        const { targetNodeId } = props;

        const targetNode = document.getElementById(targetNodeId ?? '');

        if (targetNode === null) {
            console.error(`ExpressCheckout Widget failed to find target element: ${targetNodeId}`);
            return null; // Explicitly return null
        }

        const componentInstance = ReactRenderer.render(component, props as T, targetNode, callback);

        if (componentInstance) {
            ReactRenderer.components.push(componentInstance as UpdatableComponent);
        }

        return componentInstance;
    }
}

export default ReactRenderer;
